import { canFilterByUser, FilterOption } from './filterOptions';

export const ADVANCED_FILTERS = [
	'marca',
	'stato_disp',
	'modello',
	'allestimento',
	'dealer',
	'data_disp',
	'offerta_minima',
	'colore_esterno',
	'colore_interno',
	'city',
	'data_immatricolazione',
	'alimentazione',
	'cambio',
	'trazione',
	'carrozzeria',
	'utente',
];

/** Filtri sempre in vista accanto alla ricerca, come select a valore singolo. */
export const PRIMARY_FILTERS = ['stato_corrente'];

export interface FilterLayout {
	primary: string[];
	advanced: string[];
}

/** Divide i filtri della tabella tra quelli in vista e gli avanzati. */
export const resolveFilterLayout = (
	order: string[],
	primaryIds: string[],
	availableIds: string[],
	userRole?: string
): FilterLayout => {
	const allowed = availableIds.filter(
		(id) => id !== 'utente' || canFilterByUser(userRole)
	);
	const available = new Set(allowed);
	const primary = primaryIds.filter((id) => available.has(id));
	const placed = new Set([...primary, ...order]);

	return {
		primary,
		advanced: [
			...order.filter((id) => available.has(id) && !primary.includes(id)),
			...allowed.filter((id) => !placed.has(id)),
		],
	};
};

const isActive = (value: unknown): boolean => {
	if (typeof value === 'string') return value.trim() !== '';
	if (Array.isArray(value)) return value.length > 0;
	if (value && typeof value === 'object') {
		return Object.values(value).some(isActive);
	}
	return value !== undefined && value !== null;
};

export const countActiveFilters = (
	fieldIds: string[],
	filterState: Record<string, unknown>
): number => fieldIds.filter((id) => isActive(filterState?.[id])).length;

export interface FilterChip {
	id: string;
	label: string;
	value: string;
}

const trimmed = (value: unknown) => String(value ?? '').trim();

const formatValue = (value: unknown, options?: FilterOption[]): string => {
	if (Array.isArray(value)) {
		return value
			.map(
				(item) =>
					options?.find((option) => option.value === item)?.label ?? item
			)
			.join(', ');
	}

	if (value && typeof value === 'object') {
		const { from, to } = value as { from?: string; to?: string };
		if (trimmed(from) && trimmed(to))
			return `${trimmed(from)} – ${trimmed(to)}`;
		return trimmed(from) ? `≥ ${trimmed(from)}` : `≤ ${trimmed(to)}`;
	}

	return trimmed(value);
};

/** Un chip per ogni filtro applicato, nell'ordine di `fieldIds`. */
export const activeFilterChips = (
	filterState: Record<string, unknown>,
	fieldIds: string[],
	labels: Record<string, string>,
	options: Record<string, FilterOption[]> = {}
): FilterChip[] =>
	fieldIds
		.filter((id) => isActive(filterState?.[id]))
		.map((id) => ({
			id,
			label: labels[id] ?? id,
			value: formatValue(filterState[id], options[id]),
		}));

/**
 * Quanti chip stanno su una riga larga `available`, lasciando posto al "+N"
 * se ne avanzano. Almeno uno: se non entra nemmeno il primo, si tronca.
 */
export const visibleChipCount = (
	widths: number[],
	available: number,
	moreWidth: number,
	gap: number
): number => {
	let used = 0;

	for (let i = 0; i < widths.length; i++) {
		const next = used + (i > 0 ? gap : 0) + widths[i];
		const hasRest = i < widths.length - 1;
		if (next + (hasRest ? gap + moreWidth : 0) > available)
			return Math.max(i, 1);
		used = next;
	}

	return widths.length;
};
